import React, { FormEvent, useMemo, useState } from 'react';
import {
  CookingStyle,
  DietaryRestriction,
  GroceryCategory,
  MealPlannerPreferences,
  QuantityUnit,
  StoreId,
  UserInventoryItem,
} from '../../utils/MealPlanner/types';

type WizardProps = {
  preferences: MealPlannerPreferences;
  inventory: UserInventoryItem[];
  onPreferencesChange: (preferences: MealPlannerPreferences) => void;
  onInventoryChange: (inventory: UserInventoryItem[]) => void;
  onGenerate: () => void | Promise<void>;
  isGenerating?: boolean;
};

const stores: { id: StoreId; label: string }[] = [
  { id: 'tesco', label: 'Tesco' },
  { id: 'aldi', label: 'Aldi' },
  { id: 'lidl', label: 'Lidl' },
];

const styles: { id: CookingStyle; label: string; description: string }[] = [
  { id: 'fresh-varied', label: 'Fresh & Varied', description: 'Daily cooking with ingredient packing.' },
  { id: 'leftover-rotation', label: 'Leftover Rotation', description: 'Dinner portions become next-day lunches.' },
  { id: 'batch-prep', label: 'Batch Prep', description: 'One or two bulk meals carry the week.' },
];

const dietaryOptions: { id: DietaryRestriction; label: string }[] = [
  { id: 'vegan', label: 'Vegan' },
  { id: 'vegetarian', label: 'Vegetarian' },
  { id: 'keto', label: 'Keto' },
  { id: 'gluten-free', label: 'GF' },
];

const inventorySuggestions = [
  { ingredientId: 'onion', name: 'Onion', unit: 'count', category: 'produce' },
  { ingredientId: 'rice', name: 'Rice', unit: 'g', category: 'pantry' },
  { ingredientId: 'pasta', name: 'Pasta', unit: 'g', category: 'pantry' },
  { ingredientId: 'eggs', name: 'Eggs', unit: 'count', category: 'dairy' },
  { ingredientId: 'tinned-tomatoes', name: 'Tinned tomatoes', unit: 'g', category: 'pantry' },
] as const;

function createInventoryId() {
  return `inventory-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

function MealPlannerWizard({
  preferences,
  inventory,
  onPreferencesChange,
  onInventoryChange,
  onGenerate,
  isGenerating = false,
}: WizardProps) {
  const [step, setStep] = useState(0);
  const [inventoryDraft, setInventoryDraft] = useState({
    ingredientId: 'onion',
    name: 'Onion',
    quantity: 1,
    unit: 'count' as QuantityUnit,
    category: 'produce' as GroceryCategory,
  });

  const selectedSuggestion = useMemo(
    () => inventorySuggestions.find((item) => item.ingredientId === inventoryDraft.ingredientId),
    [inventoryDraft.ingredientId]
  );

  function updatePreferences(partial: Partial<MealPlannerPreferences>) {
    onPreferencesChange({ ...preferences, ...partial });
  }

  function toggleRestriction(restriction: DietaryRestriction) {
    const active = preferences.dietaryRestrictions.includes(restriction);
    updatePreferences({
      dietaryRestrictions: active
        ? preferences.dietaryRestrictions.filter((item) => item !== restriction)
        : [...preferences.dietaryRestrictions, restriction],
    });
  }

  function submitInventory(event: FormEvent) {
    event.preventDefault();

    if (!inventoryDraft.name.trim() || inventoryDraft.quantity <= 0) {
      return;
    }

    onInventoryChange([
      ...inventory,
      {
        id: createInventoryId(),
        ingredientId: inventoryDraft.ingredientId,
        name: inventoryDraft.name.trim(),
        quantity: inventoryDraft.quantity,
        unit: inventoryDraft.unit,
        category: inventoryDraft.category,
      },
    ]);
  }

  function selectSuggestion(ingredientId: string) {
    const suggestion = inventorySuggestions.find((item) => item.ingredientId === ingredientId);
    if (!suggestion) {
      return;
    }

    setInventoryDraft({
      ingredientId: suggestion.ingredientId,
      name: suggestion.name,
      quantity: suggestion.unit === 'count' ? 1 : 250,
      unit: suggestion.unit,
      category: suggestion.category,
    });
  }

  return (
    <section className="meal-planner-panel meal-planner-wizard" aria-labelledby="planner-setup-title">
      <div className="meal-planner-stepper" aria-label="Meal planner setup steps">
        {['Budget', 'Style', 'Inventory'].map((label, index) => (
          <button
            key={label}
            type="button"
            className={step === index ? 'is-active' : ''}
            onClick={() => setStep(index)}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="meal-planner-step">
        <h2 id="planner-setup-title">Budget meal planner</h2>

        {step === 0 && (
          <div className="meal-planner-form-grid">
            <label>
              <span>Weekly cap</span>
              <input
                type="number"
                min="0"
                step="1"
                inputMode="decimal"
                value={preferences.weeklyBudgetCap}
                onChange={(event) => updatePreferences({ weeklyBudgetCap: Number(event.target.value) })}
              />
            </label>
            <label>
              <span>People</span>
              <input
                type="number"
                min="1"
                step="1"
                inputMode="numeric"
                value={preferences.people}
                onChange={(event) => updatePreferences({ people: Number(event.target.value) })}
              />
            </label>
            <div>
              <span className="meal-planner-label">Store</span>
              <div className="meal-planner-segmented">
                {stores.map((store) => (
                  <button
                    key={store.id}
                    type="button"
                    className={preferences.storeId === store.id ? 'is-active' : ''}
                    onClick={() => updatePreferences({ storeId: store.id })}
                  >
                    {store.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {step === 1 && (
          <>
            <div className="meal-planner-style-grid">
              {styles.map((style) => (
                <button
                  key={style.id}
                  type="button"
                  className={preferences.cookingStyle === style.id ? 'is-active' : ''}
                  onClick={() => updatePreferences({ cookingStyle: style.id })}
                >
                  <strong>{style.label}</strong>
                  <span>{style.description}</span>
                </button>
              ))}
            </div>
            <div className="meal-planner-chip-row" aria-label="Dietary restrictions">
              {dietaryOptions.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  className={preferences.dietaryRestrictions.includes(option.id) ? 'is-active' : ''}
                  onClick={() => toggleRestriction(option.id)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <form className="meal-planner-inventory-form" onSubmit={submitInventory}>
              <label>
                <span>Ingredient</span>
                <select value={inventoryDraft.ingredientId} onChange={(event) => selectSuggestion(event.target.value)}>
                  {inventorySuggestions.map((item) => (
                    <option key={item.ingredientId} value={item.ingredientId}>
                      {item.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span>Quantity</span>
                <input
                  type="number"
                  min="0"
                  step={selectedSuggestion?.unit === 'count' ? '0.5' : '10'}
                  inputMode="decimal"
                  value={inventoryDraft.quantity}
                  onChange={(event) => setInventoryDraft({ ...inventoryDraft, quantity: Number(event.target.value) })}
                />
              </label>
              <button type="submit">Add</button>
            </form>

            <ul className="meal-planner-inventory-list" aria-label="Current inventory">
              {inventory.map((item) => (
                <li key={item.id}>
                  <span>{item.name}</span>
                  <small>{item.quantity} {item.unit}</small>
                  <button
                    type="button"
                    aria-label={`Remove ${item.name}`}
                    onClick={() => onInventoryChange(inventory.filter((current) => current.id !== item.id))}
                  >
                    x
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>

      <div className="meal-planner-wizard-actions">
        <button type="button" disabled={step === 0} onClick={() => setStep(Math.max(0, step - 1))}>
          Back
        </button>
        {step < 2 ? (
          <button type="button" className="meal-planner-primary" onClick={() => setStep(Math.min(2, step + 1))}>
            Next
          </button>
        ) : (
          <button type="button" className="meal-planner-primary" disabled={isGenerating} onClick={onGenerate}>
            {isGenerating ? 'Generating...' : 'Generate Plan'}
          </button>
        )}
      </div>
    </section>
  );
}

export default MealPlannerWizard;
